import type { Instance } from "mobx-state-tree";
import { types, flow, getRoot, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { ClassCategory, SubClass } from "./models";
import type {
  ClassCategoryRow,
  ClassCategoryInsert,
  ClassCategoryUpdate,
  ClassCategorySnapshotIn,
  SubClassRow,
  SubClassInsert,
  SubClassUpdate,
  SubClassSnapshotIn,
} from "./models";
import type { AdminRootAccess } from "../rootAccess";

export const ClassHierarchyStore = types
  .model("ClassHierarchyStore", {
    categories: types.array(ClassCategory),
    subClassesByCategory: types.map(types.array(SubClass)),
    isLoading: types.optional(types.boolean, false),
    isLoadingSubClasses: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get categoriesList() {
      return self.categories.slice();
    },
    getCategoryById(categoryId: number) {
      return self.categories.find((c) => c.category_id === categoryId);
    },
    getSubClasses(categoryId: number): SubClassRow[] {
      const entry = self.subClassesByCategory.get(String(categoryId));
      return entry ? (entry.slice() as unknown as SubClassRow[]) : [];
    },
  }))
  .actions((self) => ({
    loadClassCategories: flow(function* loadClassCategories() {
      self.isLoading = true;
      self.error = null;
      try {
        const data: ClassCategoryRow[] = yield repo.listClassCategories();
        self.categories.clear();
        data.forEach((row) => {
          self.categories.push(row as ClassCategorySnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    loadSubClasses: flow(function* loadSubClasses(categoryId: number) {
      self.isLoadingSubClasses = true;
      self.error = null;
      try {
        const data: SubClassRow[] = yield repo.listSubClassesByCategory(categoryId);
        self.subClassesByCategory.set(
          String(categoryId),
          data.map((row) => row as SubClassSnapshotIn) as unknown as Instance<typeof SubClass>[]
        );
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoadingSubClasses = false;
      }
    }),

    createClassCategory: flow(function* createClassCategory(input: ClassCategoryInsert) {
      self.error = null;
      try {
        const created: ClassCategoryRow = yield repo.createClassCategory(input);
        self.categories.push(created as ClassCategorySnapshotIn);
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
        return created;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateClassCategory: flow(function* updateClassCategory(
      categoryId: number,
      updates: ClassCategoryUpdate
    ) {
      self.error = null;
      try {
        const updated: ClassCategoryRow = yield repo.updateClassCategory(categoryId, updates);
        const index = self.categories.findIndex((c) => c.category_id === categoryId);
        if (index !== -1) {
          self.categories[index] = cast(updated as ClassCategorySnapshotIn);
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteClassCategory: flow(function* deleteClassCategory(categoryId: number) {
      self.error = null;
      try {
        yield repo.deleteClassCategory(categoryId);
        const index = self.categories.findIndex((c) => c.category_id === categoryId);
        if (index !== -1) {
          self.categories.splice(index, 1);
        }
        self.subClassesByCategory.delete(String(categoryId));
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    createSubClass: flow(function* createSubClass(input: SubClassInsert) {
      self.error = null;
      try {
        const created: SubClassRow = yield repo.createSubClass(input);
        if (created.category_id !== null) {
          const key = String(created.category_id);
          const existing = self.subClassesByCategory.get(key);
          if (existing) {
            existing.push(created as SubClassSnapshotIn);
          }
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
        return created;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateSubClass: flow(function* updateSubClass(id: number, updates: SubClassUpdate) {
      self.error = null;
      try {
        const updated: SubClassRow = yield repo.updateSubClass(id, updates);
        // Replace the row within its bucket. category_id may have changed, so scan maps.
        for (const [key, bucket] of self.subClassesByCategory.entries()) {
          const idx = bucket.findIndex((sc) => sc.id === id);
          if (idx !== -1) {
            if (updated.category_id !== null && String(updated.category_id) !== key) {
              bucket.splice(idx, 1);
              const dest = self.subClassesByCategory.get(String(updated.category_id));
              if (dest) {
                dest.push(updated as SubClassSnapshotIn);
              }
            } else {
              bucket[idx] = cast(updated as SubClassSnapshotIn);
            }
            break;
          }
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteSubClass: flow(function* deleteSubClass(id: number) {
      self.error = null;
      try {
        yield repo.deleteSubClass(id);
        for (const bucket of self.subClassesByCategory.values()) {
          const idx = bucket.findIndex((sc) => sc.id === id);
          if (idx !== -1) {
            bucket.splice(idx, 1);
            break;
          }
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.categories.clear();
      self.subClassesByCategory.clear();
      self.isLoading = false;
      self.isLoadingSubClasses = false;
      self.error = null;
    },
  }));

export type ClassHierarchyStoreInstance = Instance<typeof ClassHierarchyStore>;
